/**
 * Maintenance history maths, ported from LoadMaintenanceHistory,
 * CollectEventsFromComponent and RenderMaintenanceTimeline in visualizer.js:
 * every subsystem's events newest first, replacements grouped per equipment
 * for the mean time between replacements, "recent" within one year.
 */
import type { Element, MaintenanceEvent, Subsystem } from '../../types';
import { DAY_MS, parseDateMs } from './format';

export interface HistoryEvent {
  subsystemId: string;
  dateMs: number;
  type: string;
  equipment: string;
  path: string;
  description: string;
  technician: string;
  workOrder: string;
  parts: string[];
  cost: number | null;
  duration: string;
}

export const EVENT_TYPE_LABEL: Record<string, string> = {
  replacement: 'Replacement',
  repair: 'Repair',
  inspection: 'Inspection',
  calibration: 'Calibration',
  upgrade: 'Upgrade',
  incident: 'Incident',
  maintenance: 'Maintenance',
};

export function eventTypeLabel(type: string): string {
  return EVENT_TYPE_LABEL[type] ?? 'Event';
}

function toEvent(s: Subsystem, path: string, e: MaintenanceEvent): HistoryEvent | null {
  const dateMs = parseDateMs(e.date);
  if (dateMs === null) return null;
  return {
    subsystemId: s.id,
    dateMs,
    type: e.type || 'maintenance',
    equipment: s.label || s.id,
    path,
    description: e.description ?? '',
    technician: e.technician ?? '',
    workOrder: e.workOrder ?? '',
    parts: e.parts ?? [],
    cost: typeof e.cost === 'number' ? e.cost : null,
    duration: e.duration ?? '',
  };
}

/** All events from every subsystem, newest first. */
export function collectEvents(element: Pick<Element, 'subsystems'>): HistoryEvent[] {
  const out: HistoryEvent[] = [];
  const visit = (s: Subsystem, trail: string[]) => {
    const path = [...trail, s.label || s.id];
    for (const e of s.events ?? []) {
      const ev = toEvent(s, path.join(' > '), e);
      if (ev) out.push(ev);
    }
    for (const child of s.childs ?? []) visit(child, path);
  };
  for (const s of element.subsystems ?? []) visit(s, []);
  return out.sort((a, b) => b.dateMs - a.dateMs);
}

/** Mean days between consecutive replacements of the same equipment, rounded; null with fewer than two for every equipment. */
export function mtbfDays(events: HistoryEvent[]): number | null {
  const grouped = new Map<string, number[]>();
  for (const e of events) {
    if (e.type !== 'replacement') continue;
    const list = grouped.get(e.equipment) ?? [];
    list.push(e.dateMs);
    grouped.set(e.equipment, list);
  }
  const gaps: number[] = [];
  for (const dates of grouped.values()) {
    if (dates.length < 2) continue;
    dates.sort((a, b) => a - b);
    for (let i = 1; i < dates.length; i++) gaps.push((dates[i] - dates[i - 1]) / DAY_MS);
  }
  if (gaps.length === 0) return null;
  return Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length);
}

export function isRecent(dateMs: number, nowMs: number): boolean {
  return nowMs - dateMs < 365 * DAY_MS;
}

export interface HistorySummary {
  total: number;
  replacements: number;
  avgMtbfDays: number | null;
  lastMs: number | null;
  recent: number;
  totalCost: number;
}

export function summariseHistory(events: HistoryEvent[], nowMs: number): HistorySummary {
  return {
    total: events.length,
    replacements: events.filter((e) => e.type === 'replacement').length,
    avgMtbfDays: mtbfDays(events),
    lastMs: events.length ? Math.max(...events.map((e) => e.dateMs)) : null,
    recent: events.filter((e) => isRecent(e.dateMs, nowMs)).length,
    totalCost: events.reduce((sum, e) => sum + (e.cost ?? 0), 0),
  };
}
